import { useState, useRef, useCallback } from "react";

interface Hotspot {
  x: number;
  y: number;
  materialId: string;
}

/**
 * Hook providing drag-to-reposition for hotspot dots on an image.
 * Returns mouse event handlers to attach to the image container
 * and individual hotspot elements.
 */
export function useDraggableHotspots(
  hotspots: Hotspot[],
  onUpdate: (updatedHotspots: Hotspot[]) => void
) {
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleDotMouseDown = useCallback(
    (e: React.MouseEvent, idx: number) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingIdx(idx);
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (draggingIdx === null || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
      const y = Math.min(100, Math.max(0, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
      const updated = [...hotspots];
      updated[draggingIdx] = { ...updated[draggingIdx], x, y };
      onUpdate(updated);
    },
    [draggingIdx, hotspots, onUpdate]
  );

  const handleMouseUp = useCallback(() => {
    setDraggingIdx(null);
  }, []);

  return {
    containerRef,
    draggingIdx,
    handleDotMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
}
